import React, { useState, useEffect, useCallback, useMemo } from'react';
import { UserPlus, Send, Copy, Trash2, RefreshCw, Loader2, CheckCircle, Clock, XCircle, Link, Calculator } from'lucide-react';
import { Button } from'../ui/button';
import { Input } from'../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from'../ui/dialog';
import { Alert, AlertDescription } from'../ui/alert';
import { toast } from'sonner';
import { API_URL } from'../../utils/api';

const statusColors = {
 pending:'bg-yellow-100 text-yellow-800',
 active:'bg-blue-100 text-blue-800',
 completed:'bg-green-100 text-green-800',
 cancelled:'bg-red-100 text-red-800'
};

const statusIcons = {
 pending: Clock,
 active: RefreshCw,
 completed: CheckCircle,
 cancelled: XCircle
};

/**
 * Calculate months between now and a deadline date
 */
const calculateMonthsUntilDeadline = (deadlineDate) => {
 if (!deadlineDate) return null;
 
 const now = new Date();
 const deadline = new Date(deadlineDate);
 
 // Calculate months difference
 let months = (deadline.getFullYear() - now.getFullYear()) * 12;
 months += deadline.getMonth() - now.getMonth();
 
 // If we're past the 1st of this month, don't count this month
 if (now.getDate() > 1) {
 months -= 1;
 }
 
 return Math.max(1, months); // At least 1 month
};

/**
 * Component for managing payment shares on a booking
 * Can be embedded in booking edit dialog or used standalone
 */
const DancerPaymentShareManager = ({ booking, authToken, onUpdate }) => {
 const [shares, setShares] = useState([]);
 const [loading, setLoading] = useState(false);
 const [showAddDialog, setShowAddDialog] = useState(false);
 const [formData, setFormData] = useState({
 dancer_name:'',
 dancer_email:'',
 share_amount:'',
 notes:''
 });
 const [submitting, setSubmitting] = useState(false);
 const [error, setError] = useState(null);

 const fetchShares = useCallback(async () => {
 if (!booking?.booking_id || !authToken) return;
 
 setLoading(true);
 setError(null);
 try {
 const response = await fetch(
 `${API_URL}/api/dancer-payments/admin/bookings/${booking.booking_id}/shares`,
 {
 headers: {'Authorization': `Bearer ${authToken}` }
 }
 );
 
 if (response.ok) {
 const data = await response.json();
 setShares(data.shares || []);
 } else {
 console.error('Failed to fetch shares:', response.status);
 setError('Failed to load payment shares');
 }
 } catch (err) {
 console.error('Failed to fetch shares:', err);
 setError('Failed to load payment shares');
 } finally {
 setLoading(false);
 }
 }, [booking, authToken]);

 useEffect(() => {
 if (booking?.booking_id && authToken) {
 fetchShares();
 }
 }, [fetchShares, booking, authToken]);

 const handleCreateShare = async () => {
 if (!formData.dancer_name || !formData.dancer_email || !formData.share_amount) {
 toast.error('Please fill in all required fields');
 return;
 }

 const amount = parseFloat(formData.share_amount);
 if (isNaN(amount) || amount <= 0) {
 toast.error('Please enter a valid amount');
 return;
 }

 setSubmitting(true);
 try {
 const response = await fetch(
 `${API_URL}/api/dancer-payments/admin/bookings/${booking.booking_id}/create-share`,
 {
 method:'POST',
 headers: {
'Content-Type':'application/json',
'Authorization': `Bearer ${authToken}`
 },
 body: JSON.stringify({
 dancer_name: formData.dancer_name,
 dancer_email: formData.dancer_email,
 share_amount: amount,
 notes: formData.notes || null
 })
 }
 );

 const data = await response.json();

 if (!response.ok) {
 throw new Error(data.detail ||'Failed to create payment share');
 }

 toast.success('Payment share created! Link sent to dancer.');
 
 // Copy link to clipboard
 if (data.payment_link) {
 navigator.clipboard.writeText(data.payment_link);
 toast.info('Payment link copied to clipboard');
 }

 setShowAddDialog(false);
 setFormData({ dancer_name:'', dancer_email:'', share_amount:'', notes:'' });
 fetchShares();
 
 if (onUpdate) onUpdate();
 } catch (err) {
 toast.error(err.message);
 } finally {
 setSubmitting(false);
 }
 };

 const handleResendLink = async (shareId) => {
 try {
 const response = await fetch(
 `${API_URL}/api/dancer-payments/admin/shares/${shareId}/resend-link`,
 {
 method:'POST',
 headers: {'Authorization': `Bearer ${authToken}` }
 }
 );

 const data = await response.json();

 if (!response.ok) {
 throw new Error(data.detail ||'Failed to resend link');
 }

 toast.success('New payment link sent!');
 
 if (data.payment_link) {
 navigator.clipboard.writeText(data.payment_link);
 toast.info('Link copied to clipboard');
 }
 } catch (err) {
 toast.error(err.message);
 }
 };

 const handleCopyLink = async (shareId) => {
 try {
 const response = await fetch(
 `${API_URL}/api/dancer-payments/admin/shares/${shareId}/get-link`,
 {
 headers: {'Authorization': `Bearer ${authToken}` }
 }
 );

 const data = await response.json();

 if (!response.ok) {
 throw new Error(data.detail ||'Failed to get link');
 }

 if (data.payment_link) {
 navigator.clipboard.writeText(data.payment_link);
 toast.success('Payment link copied to clipboard!');
 }
 } catch (err) {
 toast.error(err.message);
 }
 };

 const handleCancelShare = async (shareId) => {
 if (!window.confirm('Are you sure you want to cancel this payment share?')) return;

 try {
 const response = await fetch(
 `${API_URL}/api/dancer-payments/admin/shares/${shareId}/cancel`,
 {
 method:'POST',
 headers: {'Authorization': `Bearer ${authToken}` }
 }
 );

 if (!response.ok) {
 const data = await response.json();
 throw new Error(data.detail ||'Failed to cancel share');
 }

 toast.success('Payment share cancelled');
 fetchShares();
 
 if (onUpdate) onUpdate();
 } catch (err) {
 toast.error(err.message);
 }
 };

 const totalShares = shares.filter(s => s.status !=='cancelled').reduce((sum, s) => sum + (s.share_amount || 0), 0);
 const totalPaid = shares.filter(s => s.status !=='cancelled').reduce((sum, s) => sum + (s.amount_paid || 0), 0);

 // If no booking or auth token, don't render anything
 if (!booking?.booking_id) {
 return null;
 }

 return (
 <div className="space-y-4">
 <div className="flex items-center justify-between">
 <h4 className="font-medium text-gray-900">
 Payment Shares
 </h4>
 <Button
 size="sm"
 onClick={() => setShowAddDialog(true)}
 className="bg-purple-600 hover:bg-purple-700"
 >
 <UserPlus className="w-4 h-4 mr-1" />
 Add Dancer Share
 </Button>
 </div>

 {/* Summary */}
 {shares.length > 0 && (
 <div className="grid grid-cols-2 gap-4 p-3 bg-gray-50 rounded-lg">
 <div>
 <p className="text-sm text-gray-500">Total Shares</p>
 <p className="font-semibold">£{totalShares.toFixed(2)}</p>
 </div>
 <div>
 <p className="text-sm text-gray-500">Collected</p>
 <p className="font-semibold text-green-600">£{totalPaid.toFixed(2)}</p>
 </div>
 </div>
 )}

 {/* Shares List */}
 {loading ? (
 <div className="flex justify-center py-4">
 <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
 </div>
 ) : shares.length === 0 ? (
 <p className="text-sm text-gray-500 text-center py-4">
 No payment shares yet. Add a dancer to split the payment.
 </p>
 ) : (
 <div className="space-y-3">
 {shares.map((share) => {
 const StatusIcon = statusIcons[share.status] || Clock;
 const remaining = share.share_amount - share.amount_paid;
 
 return (
 <div
 key={share.share_id}
 className="p-3 border rounded-lg bg-white"
 >
 <div className="flex items-start justify-between">
 <div className="flex-1">
 <div className="flex items-center gap-2">
 <span className="font-medium text-gray-900">
 {share.dancer_name}
 </span>
 <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${statusColors[share.status]}`}>
 <StatusIcon className="w-3 h-3" />
 {share.status}
 </span>
 </div>
 <p className="text-sm text-gray-500">
 {share.dancer_email}
 </p>
 <div className="flex gap-4 mt-2 text-sm">
 <span>
 Share: <strong>£{share.share_amount?.toFixed(2)}</strong>
 </span>
 <span className="text-green-600">
 Paid: £{share.amount_paid?.toFixed(2)}
 </span>
 {remaining > 0 && (
 <span className="text-orange-600">
 Remaining: £{remaining.toFixed(2)}
 </span>
 )}
 </div>
 {share.monthly_amount && (
 <p className="text-xs text-gray-500 mt-1">
 Monthly: £{share.monthly_amount?.toFixed(2)}
 {share.next_payment_date && (
 <> • Next: {new Date(share.next_payment_date).toLocaleDateString('en-GB')}</>
 )}
 </p>
 )}
 </div>
 
 {share.status !=='cancelled' && share.status !=='completed' && (
 <div className="flex gap-1">
 <Button
 size="sm"
 variant="ghost"
 onClick={() => handleCopyLink(share.share_id)}
 title="Copy payment link"
 className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
 >
 <Link className="w-4 h-4" />
 </Button>
 <Button
 size="sm"
 variant="ghost"
 onClick={() => handleResendLink(share.share_id)}
 title="Resend payment link via email"
 >
 <Send className="w-4 h-4" />
 </Button>
 <Button
 size="sm"
 variant="ghost"
 onClick={() => handleCancelShare(share.share_id)}
 className="text-red-600 hover:text-red-700 hover:bg-red-50"
 title="Cancel share"
 >
 <Trash2 className="w-4 h-4" />
 </Button>
 </div>
 )}
 </div>
 </div>
 );
 })}
 </div>
 )}

 {/* Add Share Dialog */}
 <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
 <DialogContent>
 <DialogHeader>
 <DialogTitle>Add Payment Share</DialogTitle>
 <DialogDescription>
 Create a payment share for another dancer on this booking.
 They'll receive a link to set up their own payment plan.
 </DialogDescription>
 </DialogHeader>

 <div className="space-y-4 py-4">
 <div>
 <label className="block text-sm font-medium mb-1">Dancer Name *</label>
 <Input
 value={formData.dancer_name}
 onChange={(e) => setFormData(prev => ({ ...prev, dancer_name: e.target.value }))}
 placeholder="Jane Smith"
 />
 </div>

 <div>
 <label className="block text-sm font-medium mb-1">Email Address *</label>
 <Input
 type="email"
 value={formData.dancer_email}
 onChange={(e) => setFormData(prev => ({ ...prev, dancer_email: e.target.value }))}
 placeholder="jane@example.com"
 />
 </div>

 <div>
 <label className="block text-sm font-medium mb-1">Share Amount (£) *</label>
 <Input
 type="number"
 value={formData.share_amount}
 onChange={(e) => setFormData(prev => ({ ...prev, share_amount: e.target.value }))}
 placeholder="275.00"
 min="1"
 step="0.01"
 />
 </div>

 {/* Suggested installments calculator */}
 {formData.share_amount && parseFloat(formData.share_amount) > 0 && booking?.payment_deadline && (
 <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
 <div className="flex items-center gap-2 mb-2">
 <Calculator className="w-4 h-4 text-purple-600" />
 <span className="text-sm font-medium text-purple-800">Suggested Installments</span>
 </div>
 {(() => {
 const amount = parseFloat(formData.share_amount);
 const months = calculateMonthsUntilDeadline(booking.payment_deadline);
 const suggested = months ? (amount / months).toFixed(2) : null;
 const deadline = new Date(booking.payment_deadline).toLocaleDateString('en-GB', { 
 day:'numeric', month:'long', year:'numeric' 
 });
 
 return (
 <div className="text-sm text-purple-700">
 <p>Payment deadline: <strong>{deadline}</strong></p>
 <p>Months remaining: <strong>{months}</strong></p>
 <p>Suggested monthly payment: <strong>£{suggested}</strong></p>
 </div>
 );
 })()}
 </div>
 )}
 
 {formData.share_amount && parseFloat(formData.share_amount) > 0 && !booking?.payment_deadline && booking?.start_date && (
 <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
 <div className="flex items-center gap-2 mb-2">
 <Calculator className="w-4 h-4 text-purple-600" />
 <span className="text-sm font-medium text-purple-800">Suggested Installments</span>
 </div>
 {(() => {
 const amount = parseFloat(formData.share_amount);
 // Use event start date minus 1 month as deadline
 const startDate = new Date(booking.start_date);
 startDate.setMonth(startDate.getMonth() - 1);
 const months = calculateMonthsUntilDeadline(startDate.toISOString());
 const suggested = months ? (amount / months).toFixed(2) : null;
 const deadline = startDate.toLocaleDateString('en-GB', { 
 day:'numeric', month:'long', year:'numeric' 
 });
 
 return (
 <div className="text-sm text-purple-700">
 <p>Full payment by: <strong>{deadline}</strong> (1 month before event)</p>
 <p>Months remaining: <strong>{months}</strong></p>
 <p>Suggested monthly payment: <strong>£{suggested}</strong></p>
 </div>
 );
 })()}
 </div>
 )}

 <div>
 <label className="block text-sm font-medium mb-1">Notes (optional)</label>
 <Input
 value={formData.notes}
 onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
 placeholder="Any additional notes..."
 />
 </div>

 <Alert>
 <AlertDescription>
 An email will be sent to the dancer with a secure link to set up their payment plan.
 You'll also receive the link to share manually if needed.
 </AlertDescription>
 </Alert>
 </div>

 <DialogFooter>
 <Button variant="outline" onClick={() => setShowAddDialog(false)}>
 Cancel
 </Button>
 <Button
 onClick={handleCreateShare}
 disabled={submitting}
 className="bg-purple-600 hover:bg-purple-700"
 >
 {submitting ? (
 <>
 <Loader2 className="w-4 h-4 animate-spin mr-2" />
 Creating...
 </>
 ) : (
 <>
 <UserPlus className="w-4 h-4 mr-2" />
 Create & Send Link
 </>
 )}
 </Button>
 </DialogFooter>
 </DialogContent>
 </Dialog>
 </div>
 );
};

export default DancerPaymentShareManager;
